<script setup lang="ts">
    import { inject, ref, computed, watch, onBeforeUnmount } from 'vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import EventEditorDetailsNav from '@views/Event/EventEditor/Details/EventEditorDetailsNav.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { loadGoogleMaps, type GoogleAutocomplete, type GoogleMap, type GoogleMaps, type GoogleMarker } from '@shared/modules/google-maps';
    import { type Event } from '@views/Event/EventTypes';
    import { eventKey, saveEventPropertyIfChangedKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const saveEventPropertyIfChanged = inject(saveEventPropertyIfChangedKey)!;

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const address = ref('');
    const venue = ref('');
    const virtualEventUrl = ref('');

    watch(
        () => event.value?.Address,
        (value) => (address.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.Venue,
        (value) => (venue.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.VirtualEventUrl,
        (value) => (virtualEventUrl.value = value ?? ''),
        { immediate: true }
    );

    function saveAddress() {
        saveEventPropertyIfChanged('Address', address.value, $Resources.EventAddress);
    }

    const googleMapsApiKey = computed(() => contextAccess.state.currentAccount?.GoogleMapsApiKey);

    const addressInputEl = ref<HTMLInputElement | null>(null);
    const mapEl = ref<HTMLElement | null>(null);

    let maps: GoogleMaps | null = null;
    let map: GoogleMap | null = null;
    let marker: GoogleMarker | null = null;
    let autocomplete: GoogleAutocomplete | null = null;
    let isMapInitializing = false;

    watch([addressInputEl, mapEl, googleMapsApiKey], initMap);

    onBeforeUnmount(() => {
        addressInputEl.value?.removeEventListener('change', onAddressInputChange);
        if (maps && autocomplete) maps.event.clearInstanceListeners(autocomplete);
    });

    async function initMap() {
        const input = addressInputEl.value;
        const container = mapEl.value;
        const apiKey = googleMapsApiKey.value;

        if (!input || !container || !apiKey || isMapInitializing) return;

        isMapInitializing = true;

        try {
            maps = await loadGoogleMaps(apiKey);
        } catch (error) {
            console.error(error);
            isMapInitializing = false;
            return;
        }

        map = new maps.Map(container, { center: { lat: 60, lng: 20 }, zoom: 4 });

        autocomplete = new maps.places.Autocomplete(input);
        autocomplete.bindTo('bounds', map);
        autocomplete.setFields(['address_components', 'geometry', 'icon', 'name', 'formatted_address']);
        autocomplete.addListener('place_changed', onPlaceSelected);

        input.addEventListener('change', onAddressInputChange);
        onAddressInputChange();
    }

    function onPlaceSelected() {
        const place = autocomplete!.getPlace();

        if (!place.geometry || !place.formatted_address) return;

        let newAddress = place.formatted_address;

        if (place.name && place.name.toLowerCase() !== place.formatted_address.split(',')[0].toLowerCase())
            newAddress = `${place.name}, ${place.formatted_address}`;

        // Picking a suggestion doesn't blur the input, so save it right away.
        address.value = newAddress;
        saveAddress();
        showPlaceOnMap(newAddress);
    }

    function onAddressInputChange() {
        if (address.value) showPlaceOnMap(address.value);
        else removeMarker();
    }

    function showPlaceOnMap(query: string) {
        if (!maps || !map) return;

        const service = new maps.places.PlacesService(map);

        service.findPlaceFromQuery({ query, fields: ['name', 'geometry'] }, (results, status) => {
            const location = results?.[0]?.geometry?.location;

            if (status !== maps!.places.PlacesServiceStatus.OK || !location) return;

            removeMarker();
            marker = new maps!.Marker({ position: location, map: map!, animation: maps!.Animation.DROP });
            map!.setCenter(location);
            map!.setZoom(16);
        });
    }

    function removeMarker() {
        marker?.setMap(null);
        marker = null;
    }
</script>

<template>
    <div v-if="event" class="max-900">
        <h2 class="h3 m-0-t">{{ $Resources.EventLocationLabel }}?</h2>

        <div class="row">
            <div class="col-md-8">
                <FormInput
                    class="form-group"
                    :label="$Resources.EventAddress"
                    :placeholder="$Resources.EventAddress"
                    glyph="search"
                    autocomplete="off"
                    v-model="address"
                    :disabled="!canEdit"
                    @onBlur="saveAddress"
                    @inputElOnMounted="(el: HTMLInputElement) => (addressInputEl = el)" />
            </div>
            <div class="col-md-4">
                <FormInput class="form-group" :label="$Resources.EventVenue" v-model="venue" :disabled="!canEdit"
                    @onBlur="saveEventPropertyIfChanged('Venue', venue, $Resources.EventVenue)" />
            </div>
        </div>

        <FormInput
            class="form-group"
            :label="$Resources.EventVirtualUrl"
            v-model="virtualEventUrl"
            :disabled="!canEdit"
            @onBlur="saveEventPropertyIfChanged('VirtualEventUrl', virtualEventUrl, $Resources.EventVirtualUrl)" />

        <div v-if="googleMapsApiKey" ref="mapEl" style="width: 100%; height: 400px"></div>
    </div>

    <EventEditorDetailsNav v-if="event" :eventId="props.eventId" />
</template>
